import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFormPopupContext } from '../../contexts/FormPopupContext';
import { products, testimonials, awards } from '../../utils/data';
import HeroSection from '../../components/HeroSection';

const StudioSoundbar: React.FC = () => {
  const product = products.find(p => p.id === 'studio-soundbar');
  const productTestimonials = testimonials.filter(t => t.products?.includes('studio-soundbar'));
  const productAwards = awards.filter(a => a.title.includes('Design') || a.title.includes('Excellence'));
  const { openFormPopup } = useFormPopupContext();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <div className="min-h-screen">
      {/* MINIMALIST HERO SECTION */}
      <HeroSection
        desktopImage="/images/hero-section-1.png"
        mobileImage="/images/mobile-hero-1.png"
        title={
          <>
            <span className="text-gradient block">Parallax Studio</span>
            <span className="block text-3xl md:text-4xl text-secondary mt-2 font-normal">
              Soundbar
            </span>
          </>
        }
        subtitle="The Studio Soundbar embodies minimalist luxury without compromising acoustic performance. Featuring studio-grade audio processing and precision-engineered drivers, it delivers expansive soundstage and crystal-clear dialogue."
        tagline={`Investment: ${product.priceRange}`}
      >
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
        >
          <button 
            onClick={openFormPopup}
            className="btn-primary text-base sm:text-lg whitespace-nowrap"
          >
            Experience Studio Quality
          </button>
          <button 
            onClick={openFormPopup}
            className="btn-secondary text-base sm:text-lg whitespace-nowrap"
          >
            Design Consultation
          </button>
        </motion.div>
      </HeroSection>

      {/* DESIGN PHILOSOPHY SECTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                  Sophisticated Simplicity
                </h2>
                <div className="space-y-6 text-lg text-secondary/90 leading-relaxed">
                  <p>
                    Less is more, but only when that "less" is precision-engineered perfection. 
                    The Studio Soundbar's sleek architectural design seamlessly integrates into 
                    any sophisticated environment while delivering studio-grade audio performance.
                  </p>
                  <p>
                    Twelve precision-matched drivers work in perfect harmony, creating an 
                    expansive soundstage that belies its minimalist form. Advanced DSP 
                    optimization ensures every dialogue is crystal-clear, every note perfectly 
                    balanced.
                  </p>
                  <p>
                    This isn't just elegant design—it's intelligent engineering disguised 
                    as architectural art.
                  </p>
                </div>
              </motion.div>
              
              <motion.div 
                className="relative max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                {/* Clean image presentation */}
                <div className="relative group">
                  <img 
                    src="/images/parallax-cinema-soundbar-image.png"
                    alt="Parallax Studio Soundbar"
                    className="w-full h-auto rounded-lg"
                  />
                  
                  {/* Subtle hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-radial from-highlight/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
                </div>
                
                {/* Premium caption styling */}
                <motion.div 
                  className="mt-8 text-center"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="text-highlight/60 text-xs uppercase tracking-[0.3em] mb-2">
                    Handcrafted Excellence
                  </div>
                  <div className="text-secondary/40 text-sm uppercase tracking-wider">
                    Minimalist Precision
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* STUDIO-GRADE TECHNOLOGY */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-6 text-gradient">
                Studio Engineering, Refined
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Professional audio technology reimagined for the sophisticated home
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* 12 Precision Drivers */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">12 Drivers</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Precision-Matched Array</h3>
                <p className="text-secondary/80">
                  Each driver individually calibrated for perfect tonal balance 
                  and seamless integration.
                </p>
              </motion.div>
              
              {/* Studio-Grade DSP */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">Advanced DSP</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Studio-Grade Processing</h3>
                <p className="text-secondary/80">
                  Professional audio algorithms optimize every frequency for 
                  reference-quality reproduction.
                </p>
              </motion.div>

              {/* Wireless Subwoofer */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">10" Wireless Sub</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Seamless Bass Extension</h3>
                <p className="text-secondary/80">
                  Active wireless subwoofer delivers deep, controlled bass 
                  without visible connections.
                </p>
              </motion.div>

              {/* Smart Room Adaptation */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">AI Tuning</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Smart Room Adaptation</h3>
                <p className="text-secondary/80">
                  Intelligent algorithms automatically optimize sound for 
                  your specific room acoustics.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ARCHITECTURAL INTEGRATION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <motion.div 
                className="relative"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="glass-effect rounded-xl p-12">
                  <div className="space-y-6">
                    {/* Material finish options */}
                    <div className="flex gap-6 justify-center">
                      <motion.div 
                        className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-500 rounded-lg shadow-lg cursor-pointer"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                      />
                      <motion.div 
                        className="w-20 h-20 bg-gradient-to-br from-gray-800 to-black rounded-lg shadow-lg cursor-pointer"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                      />
                    </div>
                    <div className="text-center text-secondary/60 text-sm uppercase tracking-wider">
                      Premium Finish Options
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-secondary/40 mb-2">Brushed Aluminum</div>
                      <div className="text-xs text-secondary/40">Matte Black</div>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                  Seamless Design Integration
                </h2>
                <div className="space-y-6 text-lg text-secondary/90 leading-relaxed">
                  <p>
                    Every line, every curve, every finish option has been considered for 
                    perfect integration into sophisticated environments. The Studio's 
                    minimal profile and premium materials complement rather than compete 
                    with your existing design aesthetic.
                  </p>
                  <p>
                    Available in brushed aluminum or matte black finishes, with optional 
                    wall-mount hardware that disappears into your architecture. Form 
                    follows function, but both achieve perfection.
                  </p>
                </div>
                
                <div className="mt-8 grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-highlight font-semibold mb-2">Dimensions</h4>
                    <p className="text-secondary/80 text-sm">48" × 3.5" × 2.5"</p>
                  </div>
                  <div>
                    <h4 className="text-highlight font-semibold mb-2">Weight</h4>
                    <p className="text-secondary/80 text-sm">18 lbs (soundbar only)</p>
                  </div>
                  <div>
                    <h4 className="text-highlight font-semibold mb-2">Connectivity</h4>
                    <p className="text-secondary/80 text-sm">HDMI eARC, Optical, Bluetooth 5.0</p>
                  </div>
                  <div>
                    <h4 className="text-highlight font-semibold mb-2">Installation</h4>
                    <p className="text-secondary/80 text-sm">Wall-mount ready</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* PERFORMANCE SHOWCASE */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                Studio Performance, Simplified
              </h2>
              <p className="text-xl text-secondary/80 mb-16 max-w-3xl mx-auto">
                Professional-grade audio technology distilled into elegant simplicity
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-12">
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-20 h-20 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <svg className="w-10 h-10 text-highlight" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a1 1 0 011 1v7.586l3.707 3.707a1 1 0 01-1.414 1.414L17 13.414V20a1 1 0 01-1 1H8a1 1 0 01-1-1v-6.586l-3.293 3.293a1 1 0 01-1.414-1.414L7 11.414V4z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-4">Crystal-Clear Dialogue</h3>
                <p className="text-secondary/80 leading-relaxed">
                  Dedicated center channel processing ensures every word is perfectly intelligible, 
                  even in complex soundscapes.
                </p>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-20 h-20 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <svg className="w-10 h-10 text-highlight" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-4">Expansive Soundstage</h3>
                <p className="text-secondary/80 leading-relaxed">
                  Virtual surround technology creates immersive audio far beyond the soundbar's 
                  minimalist footprint.
                </p>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="w-20 h-20 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <svg className="w-10 h-10 text-highlight" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-4">Effortless Setup</h3>
                <p className="text-secondary/80 leading-relaxed">
                  Intelligent auto-calibration and wireless subwoofer deliver immediate 
                  studio-quality sound.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* TECHNICAL SPECIFICATIONS */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.h2 
              className="text-3xl md:text-4xl font-light text-center mb-12 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Engineering Specifications
            </motion.h2>
            
            <motion.div 
              className="glass-effect rounded-xl p-8 md:p-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="space-y-6">
                {Object.entries(product.specifications).map(([key, value], index) => (
                  <motion.div 
                    key={key} 
                    className="flex justify-between items-center border-b border-secondary/10 pb-4 last:border-b-0"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <span className="text-secondary/80 font-medium text-lg">{key}</span>
                    <span className="text-white font-light text-lg">{value}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS & AWARDS */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-light mb-6 text-gradient">
                Design Recognition
              </h2>
              <p className="text-xl text-secondary/80">
                Acclaimed for exceptional design and engineering excellence
              </p>
            </motion.div>

            {/* Awards */}
            {productAwards.length > 0 && (
              <div className="grid md:grid-cols-2 gap-8 mb-16">
                {productAwards.map((award, index) => (
                  <motion.div 
                    key={index}
                    className="glass-effect p-8 rounded-xl text-center"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-16 h-16 bg-highlight/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-8 h-8 text-highlight" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-accent mb-2">{award.title}</h3>
                    <p className="text-secondary/80 mb-2">{award.organization}</p>
                    <p className="text-xs text-highlight">{award.year}</p>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Testimonials */}
            {productTestimonials.length > 0 && (
              <div className="grid gap-8">
                {productTestimonials.map((testimonial, index) => (
                  <motion.div 
                    key={testimonial.id}
                    className="glass-effect p-8 rounded-xl"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex text-highlight mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-lg text-secondary/90 leading-relaxed mb-6 italic">
                      "{testimonial.content}"
                    </p>
                    <div>
                      <p className="font-semibold text-accent text-lg">{testimonial.name}</p>
                      <p className="text-secondary/80">{testimonial.role}</p>
                      {testimonial.company && (
                        <p className="text-highlight text-sm">{testimonial.company}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                Discover Studio Excellence
              </h2>
              <p className="text-xl text-secondary/80 mb-12 leading-relaxed">
                Experience how the Studio Soundbar transforms your space with 
                professional-grade audio in an elegantly minimal design. Where 
                architectural beauty meets studio-grade performance.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center max-w-2xl mx-auto">
                <button 
                  onClick={openFormPopup}
                  className="btn-primary text-lg px-12 py-4 flex-1 sm:flex-none whitespace-nowrap"
                >
                  Experience Studio Sound
                </button>
                <button 
                  onClick={openFormPopup}
                  className="btn-secondary text-lg px-12 py-4 flex-1 sm:flex-none whitespace-nowrap"
                >
                  Design Consultation
                </button>
              </div>

              <div className="mt-12 text-center">
                <p className="text-secondary/60 text-sm mb-4">
                  Professional installation and calibration included
                </p>
                <div className="inline-flex items-center gap-4 text-xs text-secondary/40 uppercase tracking-wider">
                  <span>Minimalist Design</span>
                  <span>•</span>
                  <span>Studio Performance</span>
                  <span>•</span>
                  <span>Seamless Integration</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popup handled globally */}
    </div>
  );
};

export default StudioSoundbar; 